import { use } from 'react';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

import IntercomMappingModel from 'features/device/domain/models/intercom-mapping';
import IPUnitRoomSystems from 'features/systems/ip-unit/components/ip-unit-room-systems/ip-unit-room-systems';
import useIntercomMapping from './hooks';
import styles from './intercom-mapping.module.scss';

const maximumAllowedRows = 100;

const IntercomMapping = (): React.JSX.Element => {
  const { t } = useTranslation('deviceDetails');
  const { showConfirmationPopUp } = use(ConfirmationPopupProviderContext);
  const {
    currentDevice,
    currentViewingMode,
    intercomMappings,
    currentGatewayId,
    mappingIndexToLink,
    addIntercomMapping,
    deleteIntercomMapping,
    unlinkSystem,
    validateButtonId,
    form,
    selectSystem,
    handleOnClose,
    handleOnSystemSelected,
  } = useIntercomMapping();

  const addIntercomMappingButtonSx: SxProps<Theme> = {
    '&.Mui-disabled': {
      color: Constants.Colors.primary,
      opacity: 0.38,
    },
  };

  const handleOnDeleteMapping = (index: number): void => {
    const mapping = intercomMappings[index];
    const hasValues = !!mapping.name || !!mapping.buttonId || !!mapping.systemId;

    if (!hasValues) {
      deleteIntercomMapping(index);
      return;
    }

    showConfirmationPopUp(
      () => deleteIntercomMapping(index),
      t('intercomMappingDeleteConfirmation.title'),
      t('intercomMappingDeleteConfirmation.body'),
      t('intercomMappingDeleteConfirmation.confirm'),
      t('intercomMappingDeleteConfirmation.cancel'),
    );
  };

  const viewingColumns: ColumnDefinition<IntercomMappingModel, NestedKeyof<IntercomMappingModel>>[] = [
    {
      key: 'name',
      label: t('info.intercomMappingButtonLabel'),
      disableSort: true,
      tableCellProps: { width: '33%', style: { verticalAlign: 'middle' } },
    },
    {
      key: 'buttonId',
      label: t('info.intercomMappingIdLabel'),
      disableSort: true,
      tableCellProps: { width: '33%', style: { verticalAlign: 'middle' } },
    },
    {
      key: 'systemName',
      label: t('info.intercomMappingSystemLabel'),
      disableSort: true,
      renderCustomContentProvider: (item) => <Typography variant="subtitle1">{item.systemName}</Typography>,
      tableCellProps: { width: '34%', style: { verticalAlign: 'middle' } },
    },
  ];

  const editingColumns: ColumnDefinition<IntercomMappingModel, NestedKeyof<IntercomMappingModel>>[] = [
    {
      key: 'name',
      label: t('info.intercomMappingButtonLabel'),
      disableSort: true,
      renderCustomContentProvider: (_, index): React.JSX.Element => {
        const nameField = form.register(`intercomMappings.${index}.name`, { required: true });

        return (
          <TextField
            {...nameField}
            fullWidth
            label={t('info.intercomMappingButtonLabel')}
            error={!!form.formState.errors.intercomMappings?.[index]?.name}
            helperText={form.formState.errors.intercomMappings?.[index]?.name ? t('info.requiredHelperText') : undefined}
            onBlur={(event) => {
              nameField.onBlur(event);
              void form.trigger(nameField.name);
            }}
          />
        );
      },
      tableCellProps: { width: '30%' },
    },
    {
      key: 'buttonId',
      label: t('info.intercomMappingIdLabel'),
      disableSort: true,
      renderCustomContentProvider: (_, index): React.JSX.Element => {
        const buttonIdField = form.register(`intercomMappings.${index}.buttonId`, {
          required: true,
          validate: async (buttonId: string) => {
            const validationResult = await validateButtonId(buttonId, index);
            return validationResult === true ? true : t('info.intercomMappingButtonIdNotUnique');
          },
        });

        return (
          <TextField
            {...buttonIdField}
            fullWidth
            label={t('info.intercomMappingIdLabel')}
            error={!!form.formState.errors.intercomMappings?.[index]?.buttonId}
            helperText={
              form.formState.errors.intercomMappings?.[index]?.buttonId?.message ||
              (form.formState.errors.intercomMappings?.[index]?.buttonId ? t('info.requiredHelperText') : undefined)
            }
            onBlur={(event) => {
              buttonIdField.onBlur(event);
              void form.trigger(buttonIdField.name);
            }}
          />
        );
      },
      tableCellProps: { width: '30%' },
    },
    {
      key: 'systemId',
      label: t('info.intercomMappingSystemLabel'),
      disableSort: true,
      renderCustomContentProvider: (item, index): React.JSX.Element => {
        const hasSystemError = !!form.formState.errors.intercomMappings?.[index]?.systemId;

        return (
          <div>
            <input type="hidden" {...form.register(`intercomMappings.${index}.systemId`, { required: true })} />
            <div className={styles.systemSelection}>
              <Typography variant="subtitle1">{item.systemName || t('info.noIntercomMappingSystem')}</Typography>
            </div>
            {hasSystemError && (
              <FormHelperText error sx={{ marginTop: 0 }}>
                {t('info.requiredHelperText')}
              </FormHelperText>
            )}
          </div>
        );
      },
      tableCellProps: { width: '30%', style: { verticalAlign: 'top' } },
    },
    {
      key: 'id',
      label: '',
      disableSort: true,
      renderCustomContentProvider: (item, index): React.JSX.Element => (
        <div className={styles.actionsContainer}>
          <IconButton className={styles.deleteButton} onClick={() => handleOnDeleteMapping(index)}>
            <DeleteForeverOutlinedIcon />
          </IconButton>
          {item.systemId ? (
            <IconButton className={styles.linkButton} onClick={() => unlinkSystem(index)}>
              <LinkOffIcon />
            </IconButton>
          ) : (
            <IconButton className={styles.linkButton} onClick={() => selectSystem(index)}>
              <LinkIcon />
            </IconButton>
          )}
        </div>
      ),
      tableCellProps: { width: '10%' },
    },
  ];

  const mappings = currentViewingMode === 'viewing' ? (currentDevice?.intercomMappings ?? []) : intercomMappings;
  const columns = currentViewingMode === 'viewing' ? viewingColumns : editingColumns;

  return (
    <>
      <div className={`settings-item full-width`} data-testid="intercomMappingContainer">
        <div className="header-row">
          <div className="icon-text">
            <Typography variant="h5">{t('info.intercomMappingLabel')}</Typography>
          </div>
        </div>
        {mappings.length === 0 ? (
          <Typography variant="subtitle1">{t('info.noIntercomMappings')}</Typography>
        ) : (
          <Table
            className={styles.table}
            columns={columns}
            data={mappings.map<RowItem<IntercomMappingModel>>((item) => ({ data: item, showColorOnHover: false }))}
            rowIdentifier={(item) => item.id}
            sx={{
              '& .MuiTableHead-root .MuiTableCell-root:first-of-type': {
                paddingLeft: '8px !important',
              },
            }}
          />
        )}
        {currentViewingMode !== 'viewing' && (
          <>
            <div className={styles.addIntercomMappingContainer}>
              <Button
                disabled={intercomMappings.length >= maximumAllowedRows}
                startIcon={<AddCircleOutlineRounded color="primary" />}
                sx={addIntercomMappingButtonSx}
                variant="text"
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={addIntercomMapping}
              >
                {t('info.addIntercomMapping')}
              </Button>
            </div>
            <IPUnitRoomSystems
              gatewayId={currentGatewayId}
              isOpen={mappingIndexToLink !== undefined}
              unavailableSystemIds={intercomMappings.filter((mapping) => mapping.systemId).map((mapping) => mapping.systemId ?? '')}
              onClose={handleOnClose}
              onSystemSelected={handleOnSystemSelected}
            />
          </>
        )}
      </div>
      {mappings.length === 0 && <Divider />}
    </>
  );
};

export default IntercomMapping;
